import i18n from 'i18next';
import HttpBackend from 'i18next-http-backend';
import { initReactI18next } from 'react-i18next';

// English translations (bundled eagerly — no HTTP request needed)
import commonEn from './locales/en/common.json';
import editorEn from './locales/en/editor.json';
import errorsEn from './locales/en/errors.json';
import pipelineEn from './locales/en/pipeline.json';
import seoEn from './locales/en/seo.json';
import settingsEn from './locales/en/settings.json';
import sidebarEn from './locales/en/sidebar.json';
import toolsEn from './locales/en/tools.json';

export const SUPPORTED_LANGUAGES = ['en', 'es', 'de', 'fr', 'hi', 'ja', 'pt-br'] as const;
export const DEFAULT_LANGUAGE = 'en';
const FALLBACK_LANGUAGE = 'en';

export const NAMESPACES = [
  'common',
  'editor',
  'sidebar',
  'tools',
  'pipeline',
  'settings',
  'errors',
  'seo',
] as const;

let initialized = false;

/**
 * Initialize i18n. Must be called AFTER React is available (not at module scope)
 * to avoid TDZ errors in production builds where initReactI18next accesses React.
 */
export function initI18n(): typeof i18n {
  if (initialized) return i18n;
  initialized = true;

  i18n
    .use(HttpBackend)
    .use(initReactI18next)
    .init({
      lng: DEFAULT_LANGUAGE,
      fallbackLng: FALLBACK_LANGUAGE,
      supportedLngs: [...SUPPORTED_LANGUAGES],

      ns: [...NAMESPACES],
      defaultNS: 'common',

      // Eagerly bundled English resources
      resources: {
        en: {
          common: commonEn,
          editor: editorEn,
          sidebar: sidebarEn,
          tools: toolsEn,
          pipeline: pipelineEn,
          settings: settingsEn,
          errors: errorsEn,
          seo: seoEn,
        },
      },

      // HTTP backend for lazy-loaded non-English languages
      backend: {
        loadPath: '/locales/{{lng}}/{{ns}}.json',
        allowMultiLoading: false,
        queryStringParams: { v: '1.0' },
      },

      interpolation: {
        escapeValue: false, // React already escapes
      },

      debug: import.meta.env.DEV,

      saveMissing: false,
      missingKeyHandler: (lngs, ns, key) => {
        if (import.meta.env.DEV) {
          console.warn(`[i18n] Missing translation: ${String(lngs)}:${ns}.${key}`);
        }
      },

      react: {
        useSuspense: false,
      },
    });

  return i18n;
}

export default i18n;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];
export type Namespace = (typeof NAMESPACES)[number];
